import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { BASE_URL } from '../config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faSearch,
  faSyncAlt,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';

const UploadCardFeeHistory = () => {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    original_file_name: '',
    file_type: '',
    status: '',
    created_at: '',
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc',
  });

  const statusMap = {
    uploaded: { name: 'Uploaded', color: 'bg-blue-100 text-blue-800' },
    processing: { name: 'Processing', color: 'bg-yellow-100 text-yellow-800' },
    completed: { name: 'Completed', color: 'bg-green-100 text-green-800' },
    failed: { name: 'Failed', color: 'bg-red-100 text-red-800' },
  };

  const columns = [
    { key: 'original_file_name', label: 'File Name', sortable: true },
    { key: 'file_type', label: 'Type', sortable: true },
    { key: 'file_size', label: 'Size', sortable: true },
    { key: 'created_at', label: 'Uploaded At', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'success_insert_count', label: 'Success Insert Count', sortable: true },
    { key: 'failed_insert_count', label: 'Failed Insert Count', sortable: true },
    { key: 'actions', label: 'Actions', sortable: false },
  ];

  useEffect(() => {
    fetchUploadHistory();
  }, [filters, sortConfig, currentPage, itemsPerPage, searchTerm]);

  const fetchUploadHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/card-replacement-fee/uploads`, {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
          ...filters,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authentication/token')}`,
        },
      });
      setUploads(response.data.data.data);
      setTotalPages(response.data.data.last_page);
    } catch (error) {
      if (error.response?.status === 401) window.location.href = '/login';
      if (error.response?.status === 403) {
        Swal.fire('Error!', 'You do not have permission to access this.', 'error');
      }
      if (error.response?.status === 429) {
        Swal.fire('Error!', 'Rate limit exceeded. Please try again later.', 'error');
      }
      Swal.fire('Error!', 'Failed to fetch upload history.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    if (currentPage <= 3) endPage = Math.min(5, totalPages);
    else if (currentPage >= totalPages - 2) startPage = Math.max(totalPages - 4, 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded-lg text-sm transition-all duration-200 ${
            currentPage === i ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-indigo-100'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const LoadingSkeleton = ({ rows = 5, columns = 8 }) => (
    <>
      {Array(rows)
        .fill()
        .map((_, i) => (
          <tr key={i} className="animate-pulse border-b">
            {Array(columns)
              .fill()
              .map((_, j) => (
                <td key={j} className="p-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </td>
              ))}
          </tr>
        ))}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-1xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-white rounded-2xl shadow-lg">
            <FontAwesomeIcon icon={faSyncAlt} className="text-indigo-600 text-lg" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Uploaded File Card Replacement Fee History</h1>
            <p className="text-sm text-gray-500">View and manage your uploaded card fee files</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Top Controls */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="relative flex-1">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search files..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  aria-label="Search files"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={fetchUploadHistory}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-all duration-200 shadow-sm"
                >
                  <FontAwesomeIcon icon={faSyncAlt} />
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 bg-gray-50 border-b border-gray-100 overflow-x-auto">
            <div className="flex gap-4 min-w-max">
              {columns.map(
                (col) =>
                  col.key !== 'actions' &&
                  col.key !== 'file_size' &&
                  col.key !== 'success_insert_count' &&
                  col.key !== 'failed_insert_count' && (
                    <div key={col.key} className="flex-1 min-w-[150px]">
                      {col.key === 'status' ? (
                        <select
                          value={filters[col.key]}
                          onChange={(e) => handleFilterChange(col.key, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                          aria-label="Filter by status"
                        >
                          <option value="">All Statuses</option>
                          {Object.entries(statusMap).map(([key, { name }]) => (
                            <option key={key} value={key}>
                              {name}
                            </option>
                          ))}
                        </select>
                      ) : col.key === 'created_at' ? (
                        <input
                          type="date"
                          value={filters[col.key]}
                          onChange={(e) => handleFilterChange(col.key, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                          aria-label="Filter by uploaded at"
                        />
                      ) : (
                        <input
                          type="text"
                          placeholder={`Filter by ${col.label}`}
                          value={filters[col.key]}
                          onChange={(e) => handleFilterChange(col.key, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                          aria-label={`Filter by ${col.label}`}
                        />
                      )}
                    </div>
                  )
              )}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`p-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                        column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                      } ${
                        ['success_insert_count', 'failed_insert_count'].includes(column.key)
                          ? 'min-w-[120px]'
                          : ''
                      }`}
                      onClick={() => column.sortable && handleSort(column.key)}
                      aria-sort={
                        sortConfig.key === column.key
                          ? sortConfig.direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      <div className="flex items-center gap-1">
                        {column.label}
                        {column.sortable && (
                          <div className="flex flex-col text-xs">
                            <span
                              className={`${
                                sortConfig.key === column.key && sortConfig.direction === 'asc'
                                  ? 'text-indigo-600'
                                  : 'text-gray-400'
                              }`}
                            >
                              ▲
                            </span>
                            <span
                              className={`${
                                sortConfig.key === column.key && sortConfig.direction === 'desc'
                                  ? 'text-indigo-600'
                                  : 'text-gray-400'
                              }`}
                            >
                              ▼
                            </span>
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <LoadingSkeleton rows={itemsPerPage} columns={columns.length} />
                ) : uploads.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="p-3 text-center text-gray-500 text-sm">
                      No uploads

 found.
                    </td>
                  </tr>
                ) : (
                  uploads.map((upload) => (
                    <tr key={upload.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-sm">{upload.original_file_name}</td>
                      <td className="p-3 text-sm">{upload.file_type.split('/').pop()}</td>
                      <td className="p-3 text-sm">
                        {(upload.file_size / 1024 / 1024).toFixed(2)} MB
                      </td>
                      <td className="p-3 text-sm">
                        {new Date(upload.created_at).toLocaleString()}
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            statusMap[upload.status]?.color || 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {statusMap[upload.status]?.name || 'Unknown'}
                        </span>
                      </td>
                      <td className="p-3 text-sm">{upload.success_insert_count || 0}</td>
                      <td className="p-3 text-sm">{upload.failed_insert_count || 0}</td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <a
                            href={upload.signed_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 text-xs text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-all shadow-sm"
                          >
                            <FontAwesomeIcon icon={faDownload} className="mr-1" />
                            Download
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row md:items-center justify-between p-6 border-t border-gray-100">
            <div className="mb-3 md:mb-0">
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                aria-label="Items per page"
              >
                {[5, 10, 20, 50, 100].map((val) => (
                  <option key={val} value={val}>
                    {val} per page
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || loading}
                className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-all"
                aria-label="First page"
              >
                <FontAwesomeIcon icon={faAngleDoubleLeft} />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-all"
                aria-label="Previous page"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              {renderPageNumbers()}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-all"
                aria-label="Next page"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || loading}
                className="p-2 text-gray-700 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition-all"
                aria-label="Last page"
              >
                <FontAwesomeIcon icon={faAngleDoubleRight} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadCardFeeHistory;